import { Tooltip } from '@/components/Tooltip';
import {
    ELoanUsage,
    selectLoanTerm,
    selectLoanUsage,
    setBalloon,
    setLoanTerm,
    setLoanUsage,
} from '@/context/borrower';
import { selectIsLoading, selectIsNZ, selectPartner } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { getNumber } from '@/utils/valueFormat';

import { LoanTermDisabled } from '../LoanTermDisabled';

export interface IProps {
    baseId: string;
}

export const testId = 'LoanTerm';

export function LoanTerm({ baseId }: IProps): JSX.Element {
    // ***** Redux *****
    const currentState = useAppSelector(selectLoanTerm);
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectIsLoading);
    const partner = useAppSelector(selectPartner);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isNZ = useAppSelector(selectIsNZ);

    const minLoanTerm = partner?.minLoanTerm || 1;
    const maxLoanTerm = isNZ ? 5 : partner?.maxLoanTerm || 7;

    // ***** Event Handlers *****
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const value = event.target.value;
        const intValue = getNumber(value, maxLoanTerm);
        if (intValue === currentState) return;
        dispatch(setLoanTerm(intValue));
        if (intValue > 5) {
            if (loanUsage === ELoanUsage.NOVATED) {
                dispatch(setLoanUsage(ELoanUsage.PERSONAL));
            }
        }
    }

    if (currentState > maxLoanTerm) {
        dispatch(setBalloon(0));
    }

    const currentPercent = ((currentState - 1) / (maxLoanTerm - 1)) * 100;

    if (minLoanTerm === maxLoanTerm) {
        return <LoanTermDisabled baseId={baseId} />;
    }

    // ***** Render *****
    return (
        <div className='pb-2' data-testid={testId}>
            <Tooltip content='The longer the term the lower the repayments.'>
                <h1 className='fieldHeading'>
                    What&apos;s your preferred loan term?
                </h1>
            </Tooltip>

            <div className='flex-center flex-col'>
                <label
                    className='font-bold text-lg pb-1'
                    htmlFor={`${baseId}-input-loanTerm`}
                >
                    {currentState} years
                </label>
                <div className='relative w-full h-1 rounded-full'>
                    <div
                        className='absolute top-0 h-full bg-primary rounded-full'
                        style={{ width: `${currentPercent}%` }}
                    ></div>
                    <div
                        className='absolute top-0 h-full bg-gray-300 rounded-full'
                        style={{
                            width: `${100 - currentPercent}%`,
                            left: `${currentPercent}%`,
                        }}
                    ></div>
                    <input
                        id={`${baseId}-input-loanTerm`}
                        type='range'
                        min={1}
                        max={maxLoanTerm}
                        value={currentState}
                        className='absolute w-full h-full cursor-pointer range-sm top-1'
                        step='1'
                        onChange={handleChange}
                        disabled={isLoading ? true : false}
                        style={{
                            WebkitAppearance: 'none',
                            background: 'transparent',
                            outline: 'none',
                            height: '20px', // increase the height to match the height of the thumb
                            left: 0,
                        }}
                    />
                    <style>
                        {`
                        #${baseId}-input-loanTerm::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            width: 20px;
                            height: 20px;
                            border-radius: 10px;
                            background: var(--primaryColor);
                            cursor: pointer;
                            margin-top: -8px;
                            position: relative;
                            z-index: 0;
                        }

                        #${baseId}-input-loanTerm::-moz-range-thumb {
                            width: 20px;
                            height: 20px;
                            border-radius: 10px;
                            background: var(--primaryColor);
                            cursor: pointer;
                            z-index: 2;
                        }
                    `}
                    </style>
                </div>
            </div>
        </div>
    );
}
